import React, {useState, useContext, FC} from 'react'
import {useForm} from 'react-hook-form'
import Cookies from 'js-cookie'
import {AuthContext} from 'App'
import {verifyPassword, SignInParams} from 'api/auth'
import client from 'api/client'
import {Input} from 'components/Input/Input'
import {Button} from 'components/Button'
import {yupResolver} from '@hookform/resolvers/yup'
import {schema} from './schema'
import styles from './index.module.css'

interface User {
  userName: string
  email: string
  cdmNo?: string
  encryptedDamtomoId: string
  shukeiId: string
}

interface FormValues {
  userName: string
  email: string
  cdmNo?: string
  encryptedDamtomoId: string
  shukeiId: string
}

export const UserInfo: FC = () => {
  const [showModal, setShowModal] = useState(false);
  const [editUserData, setEditUserData] = useState<FormValues | null>(null);
  const [password, setPassword] = useState('');

  const {currentUser, handleGetCurrentUser} = useContext(AuthContext) as {
    currentUser: User
    handleGetCurrentUser: () => Promise<void>
  };

  const verifyPasswordAndUpdate = async (data: FormValues, password: string) => {
    const signInProps: SignInParams = {
      login: currentUser.email,
      password: password,
    }
    const isAuthenticated = await verifyPassword(signInProps)
    if (isAuthenticated) {
      sendUpdateRequest(data)
    } else {
      alert('パスワードが誤っています')
    }
  }

  const sendUpdateRequest = (data: FormValues) => {
    const headers = {
      'access-token': Cookies.get('_access_token'),
      client: Cookies.get('_client'),
      uid: Cookies.get('_uid'),
      'Content-Type': 'application/json',
    }
  
    const payload = {
      user: {
        user_name: data.userName,
        email: data.email,
        cdm_no: data.cdmNo,
        encrypted_damtomo_id: data.encryptedDamtomoId,
        shukei_id: data.shukeiId,
      },
    }
  
    client.patch('/api/v1/users/update', payload, {headers})
      .then((response: any) => {
        if (response.status === 200) {
          alert(response.data.message);
          handleGetCurrentUser();
          setShowModal(false);
          setPassword('')
        } else {
          console.error('更新に失敗しました');
        }
      })
      .catch((error: any) => {
        console.log(error);
        alert('更新中に問題が発生しました。' + (error.response?.data?.errors ?? ''));
      });
  }

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<FormValues>({
    resolver: yupResolver(schema),
    mode: 'onChange',
    defaultValues: {
      userName: currentUser.userName,
      email: currentUser.email,
      cdmNo: currentUser.cdmNo,
      encryptedDamtomoId: currentUser.encryptedDamtomoId,
      shukeiId: currentUser.shukeiId,
    }
  });

  const onSubmit = (data: FormValues) => {
    setEditUserData(data);
    setShowModal(true);
  };

  return (
    <div className={styles.formContainer}>
      <h1>ユーザー設定</h1>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Input
          label='ユーザ名'
          isError={!!errors.userName}
          register={register('userName', { required: true })}
        />
        {errors.userName && <p className={styles.errorText}>{errors.userName?.message}</p>}

        <Input
          label='メールアドレス'
          isError={!!errors.email}
          register={register('email', { required: true })}
        />
        {errors.email && <p className={styles.errorText}>{errors.email?.message}</p>}

        <Input
          label="cdm番号"
          isError={!!errors.cdmNo}
          register={register("cdmNo")}
        />
        {errors.cdmNo && <p className={styles.errorText}>{errors.cdmNo?.message}</p>}

        <Input
          label='暗号化されたダムともID'
          isError={!!errors.encryptedDamtomoId}
          register={register("encryptedDamtomoId", { required: true })}
        />
        {errors.encryptedDamtomoId && <p className={styles.errorText}>{errors.encryptedDamtomoId?.message}</p>}

        <Input
          label='精密集計id'
          isError={!!errors.shukeiId}
          register={register("shukeiId", { required: true })}
        />
        {errors.shukeiId && <p className={styles.errorText}>{errors.shukeiId?.message}</p>}

        <Button primary disabled={Object.keys(errors).length > 0}>
          変更する
        </Button>
      </form>

      {showModal &&
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <h2>パスワード確認</h2>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (editUserData) {
                  verifyPasswordAndUpdate(editUserData, password);
                }
              }}
            >
              <input
                autoFocus
                className={styles.passwordInput}
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="パスワードを入力"
                required
              />
              <div className={styles.modalButtons}>
                <Button primary>送信</Button>
                <Button
                  type='button'
                  secondary
                  onClick={() => {
                    setPassword('')
                    setShowModal(false)
                  }}
                >キャンセル</Button>
              </div>
            </form>
          </div>
        </div>
      }
    </div>
  );
};
